import { TopicsFilter, TopicsSortOrder } from "@/types/discussions";
import BaseApiClient from "./base-client";
export const DEFAULT_TOPIC_PAGE_SIZE = -1;
export const DEFAULT_POST_PAGE_SIZE = 500;
export const DEFAULT_PAGE_SIZE = 25;
export const DEFAULT_REPLIES_PAGE_SIZE = 25;
export const DEFAULT_REACTOR_USERS_LIMIT = 5;
class DiscussionsApi extends BaseApiClient {
    FORUMS_BASE_PATH = "/api/discussions/forums";
    getForums = () => this.get(this.FORUMS_BASE_PATH);
    getForumById = (forumId) => this.get(`${this.FORUMS_BASE_PATH}/${forumId}`);
    createForum = (forum) => this.post(this.FORUMS_BASE_PATH, {
        data: {
            name: forum.name,
            description: forum.description,
            ...forum.config
        }
    });
    updateForum = (forum) => this.put(`${this.FORUMS_BASE_PATH}/${forum.forumId}`, {
        data: {
            name: forum.name,
            description: forum.description,
            ...forum.config
        }
    });
    deleteForum = (forum) => this.delete(`${this.FORUMS_BASE_PATH}/${forum.forumId}`);
    getForumViewStateById = (forumId) => this.get(`${this.FORUMS_BASE_PATH}/${forumId}/viewstate`);
    updateForumViewState = (forumId, viewState) => {
        return this.put(`${this.FORUMS_BASE_PATH}/${forumId}/viewstate`, {
            data: viewState
        });
    };
    getTopics = ({ forumId, hierarchyCategorySearch, categoryId, start = 1, size = DEFAULT_TOPIC_PAGE_SIZE, filter = TopicsFilter.ALL, sortOrder = TopicsSortOrder.RECENT_POSTS, keyword = "" }) => {
        return this.get(`${this.FORUMS_BASE_PATH}/${forumId}/topics`, {
            params: {
                categoryId,
                hierarchyCategorySearch,
                start,
                size,
                filter,
                sort: sortOrder,
                keyword
            }
        });
    };
    getTopicById = (forumId, topicId) => this.get(`${this.FORUMS_BASE_PATH}/${forumId}/topics/${topicId}`);
    createTopic = (topic) => this.post(`${this.FORUMS_BASE_PATH}/${topic.forumId}/topics`, {
        data: {
            subject: topic.subject,
            postBody: topic.postBody,
            categoryId: topic.categoryId
        }
    });
    deleteTopic = (topic) => this.delete(`${this.FORUMS_BASE_PATH}/${topic.forumId}/topics/${topic.topicId}`);
    updateTopic = (data) => this.patch(`${this.FORUMS_BASE_PATH}/${data.forumId}/topics/${data.topicId}`, { data });
    getPosts = ({ forumId, topicId, start, size, modifiedSince }) => {
        return this.get(`${this.FORUMS_BASE_PATH}/${forumId}/topics/${topicId}/posts`, {
            params: {
                start,
                size,
                modifiedSince
            }
        });
    };
    getPostsStartingFromId = ({ forumId, topicId, postId, size }) => {
        return this.get(`${this.FORUMS_BASE_PATH}/${forumId}/topics/${topicId}/posts`, {
            params: {
                postId,
                size
            }
        });
    };
    getPost = ({ forumId, topicId, postId }) => {
        return this.get(`${this.FORUMS_BASE_PATH}/${forumId}/topics/${topicId}/posts/${postId}`);
    };
    createPost = (forumId, topicId, { postBody, referencedPostIds }) => this.post(`${this.FORUMS_BASE_PATH}/${forumId}/topics/${topicId}/posts`, {
        data: {
            postBody,
            referencedPostIds
        }
    });
    updatePost = (forumId, topicId, { postId, postBody, referencedPostIds }) => this.put(`${this.FORUMS_BASE_PATH}/${forumId}/topics/${topicId}/posts/${postId}`, {
        data: {
            postBody,
            referencedPostIds
        }
    });
    deletePost = (post) => this.delete(`${this.FORUMS_BASE_PATH}/${post.forumId}/topics/${post.topicId}/posts/${post.postId}`);
    getPostReplies = ({ forumId, topicId, postId, start = 1, size = DEFAULT_REPLIES_PAGE_SIZE }) => {
        return this.get(`${this.FORUMS_BASE_PATH}/${forumId}/topics/${topicId}/posts/${postId}/replies`, {
            params: {
                start,
                size
            }
        });
    };
    updateLastViewedPostId = (forumId, topicId, lastViewedPostId) => this.patch(`${this.FORUMS_BASE_PATH}/${forumId}/topics/${topicId}/viewstate`, {
        data: {
            lastViewedPostId
        }
    });
    updateFollowLevel = (forumId, topicId, followLevel) => this.patch(`${this.FORUMS_BASE_PATH}/${forumId}/topics/${topicId}/viewstate`, {
        data: {
            followLevel
        }
    });
    findCategories = (forumId) => {
        return this.get(`${this.FORUMS_BASE_PATH}/${forumId}/categories`);
    };
    getCategory = (forumId, categoryId) => {
        return this.get(`${this.FORUMS_BASE_PATH}/${forumId}/categories/${categoryId}`);
    };
    createCategory = (category) => {
        return this.post(`${this.FORUMS_BASE_PATH}/${category.forumId}/categories`, {
            data: category
        });
    };
    updateCategory = (category) => {
        return this.patch(`${this.FORUMS_BASE_PATH}/${category.forumId}/categories/${category.categoryId}`, {
            data: category
        });
    };
    deleteCategory = (category, destinationCategoryId) => {
        const path = `${this.FORUMS_BASE_PATH}/${category.forumId}/categories/${category.categoryId}`;
        const query = destinationCategoryId !== undefined ? "?destinationCategoryId=" + String(destinationCategoryId) : "";
        return this.delete(`${path}${query}`, { data: category });
    };
    setReactions = ({ forumId, topicId, postId, created, removed }) => this.patch(`${this.FORUMS_BASE_PATH}/${forumId}/topics/${topicId}/posts/${postId}/reaction`, {
        data: {
            created,
            removed
        }
    });
    triggerExport = async (exportFilter) => {
        return this.get(`${this.FORUMS_BASE_PATH}/export`, {
            params: { ...exportFilter }
        });
    };
    checkExportProgress = async (exportId) => {
        return this.get("/api/export/progress/" + exportId);
    };
    getQnADiscussions = async () => {
        return this.get(`/api/qna/filters/topics`);
    };
    static instance = new DiscussionsApi();
}
export default DiscussionsApi.instance;
